import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiMessageSquare, FiPlus, FiSend, FiArrowLeft } from 'react-icons/fi';
import api from '../../services/api';

const ParentMessagesPage = () => {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const loadThreads = () => {
    api.get('/parent-portal/messages/threads')
      .then((res) => { if (res.data.success) setThreads(res.data.data); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadThreads(); }, []);

  const openThread = async (id) => {
    try {
      const res = await api.get(`/parent-portal/messages/threads/${id}`);
      if (res.data.success) {
        setActiveThread(res.data.thread);
        setMessages(res.data.messages);
        loadThreads();
      }
    } catch {
      toast.error('Erreur');
    }
  };

  const createThread = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post('/parent-portal/messages/threads', { subject: newSubject, body: newBody });
      if (res.data.success) {
        toast.success('Message envoyé');
        setShowNew(false);
        setNewSubject('');
        setNewBody('');
        loadThreads();
        openThread(res.data.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !activeThread) return;
    setSending(true);
    try {
      await api.post(`/parent-portal/messages/threads/${activeThread.id}/reply`, { body: replyBody });
      setReplyBody('');
      openThread(activeThread.id);
      toast.success('Réponse envoyée');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Messagerie</h2>
          <p className="text-sm text-slate-500">Échangez avec l'établissement</p>
        </div>
        {!activeThread && !showNew && (
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
          >
            <FiPlus size={14} /> Nouveau message
          </button>
        )}
      </div>

      {showNew && (
        <form onSubmit={createThread} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h3 className="font-semibold text-slate-900">Nouvelle conversation</h3>
          <input
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            placeholder="Objet"
            required
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="Votre message..."
            required
            rows={4}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button type="submit" disabled={sending} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50">
              Envoyer
            </button>
            <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-slate-600 text-sm">Annuler</button>
          </div>
        </form>
      )}

      {activeThread ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
            <button onClick={() => { setActiveThread(null); setMessages([]); }} className="text-slate-500 hover:text-slate-700">
              <FiArrowLeft size={18} />
            </button>
            <div>
              <h3 className="font-semibold text-slate-900">{activeThread.subject}</h3>
              <p className="text-xs text-slate-500">
                {activeThread.status === 'closed' ? 'Conversation fermée' : 'Ouverte'}
              </p>
            </div>
          </div>
          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {messages.map((m) => {
              const isParent = m.sender_role === 'parent';
              return (
                <div key={m.id} className={`flex ${isParent ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                    isParent ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-800'
                  }`}>
                    <p className="text-xs opacity-70 mb-1">{m.sender_prenom} {m.sender_nom}</p>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <p className="text-[10px] opacity-60 mt-1">
                      {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {activeThread.status !== 'closed' && (
            <form onSubmit={sendReply} className="p-4 border-t border-slate-100 flex gap-2">
              <input
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Votre réponse..."
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
              <button type="submit" disabled={sending} className="px-4 py-2 bg-primary-600 text-white rounded-lg">
                <FiSend size={16} />
              </button>
            </form>
          )}
        </div>
      ) : (
        !showNew && (
          threads.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-slate-500">
              <FiMessageSquare className="mx-auto mb-3 text-3xl text-slate-300" />
              Aucune conversation. Contactez l'établissement.
            </div>
          ) : (
            <div className="space-y-2">
              {threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => openThread(t.id)}
                  className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{t.subject}</p>
                      <p className="text-sm text-slate-500 truncate mt-0.5">{t.last_preview}</p>
                    </div>
                    {t.unread_count > 0 && (
                      <span className="bg-primary-600 text-white text-xs px-2 py-0.5 rounded-full">{t.unread_count}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString('fr-FR') : ''}
                    {t.status === 'closed' && ' — Fermée'}
                  </p>
                </button>
              ))}
            </div>
          )
        )
      )}
    </div>
  );
};

export default ParentMessagesPage;
